/**
 * usePageContext — the single source of "what page is the user on" for the
 * assistant. Feeds the chat transport body, the navigation-hint recorder, and
 * the "On this page" panel, so the three can never disagree.
 *
 * The whole context is resolved inside the router-state selector and compared
 * as a serialized string. Selecting only `location.href` is a trap: the
 * router updates `location` before the new `matches` commit, so an
 * href-keyed subscription reads stale matches and never re-fires when they
 * land (matches-commit doesn't change the href).
 */

import React from 'react';
import { useRouterState } from '@tanstack/react-router';
import { resolvePageContext, type ResolvedPageContext } from './pageRegistry';

export function usePageContext(): ResolvedPageContext {
  const contextKey = useRouterState({
    // Cheap pure resolution (a few map lookups); the string result gives the
    // store's Object.is comparison a stable identity, so we re-render exactly
    // when the resolved page context actually changes.
    select: (state) =>
      JSON.stringify(
        resolvePageContext(
          state.matches.map((match) => ({
            routeId: String(match.routeId),
            params: match.params as Record<string, unknown>,
          })),
          state.location.pathname,
          (state.location.search ?? {}) as Record<string, unknown>
        )
      ),
  });

  return React.useMemo(() => JSON.parse(contextKey) as ResolvedPageContext, [contextKey]);
}
